import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useTableNavigation } from '../hooks/useTableNavigation';
import { useDraft } from '../hooks/useDraft';
import DraftStatusBadge from './DraftStatusBadge';

import { createEmptyRow, getDesignColors, getSizeGroups, sortScannedRows, computeGrandTotals } from './create-purchase-entry/helpers';
import PEHeader from './create-purchase-entry/PEHeader';
import VendorDetailsCard from './create-purchase-entry/VendorDetailsCard';
import PEScannerCard from './create-purchase-entry/PEScannerCard';
import InvoiceTable from './create-purchase-entry/InvoiceTable';
import FreightFooter from './create-purchase-entry/FreightFooter';
import SubstituteDialog from './create-purchase-entry/SubstituteDialog';
import CostHistoryDialog from './create-purchase-entry/CostHistoryDialog';

const CreatePurchaseEntry = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editPE = location.state?.editPE || null;
  const fromPO = location.state?.fromPO || null;

  // Master data
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [] });
  const [vendorPOs, setVendorPOs] = useState([]);

  // Form state
  const [selectedVendor, setSelectedVendor] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [peNotes, setPeNotes] = useState('');

  // Table rows
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [saving, setSaving] = useState(false);
  const [freightCharges, setFreightCharges] = useState('');

  // Draft auto-save
  const { draftStatus, save: saveDraftData, clear: clearDraft } = useDraft('PE', { enabled: !editPE && !fromPO, initialDraftId: location.state?.draftId || null });

  // Design substitute dialog
  const [substituteDialog, setSubstituteDialog] = useState(null);
  const [substituteSearch, setSubstituteSearch] = useState('');
  // Cost history popup
  const [costHistoryPopup, setCostHistoryPopup] = useState(null);

  const vendorRef = useRef(null);
  const handleTableKeyDown = useTableNavigation('pe-invoice-table');

  // Scanner
  const [scannerActive, setScannerActive] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = 'pe-qr-reader';
  const productsRef = useRef([]);
  const rowsRef = useRef(rows);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Scanner handlers
  const handleScannedCode = useCallback((code) => {
    const prods = productsRef.current;
    let scanned = code.trim().toUpperCase();
    try { const qr = JSON.parse(code.trim()); if (qr.design) scanned = qr.design.toUpperCase(); } catch {}
    let product = prods.find(p => p.design_code?.toUpperCase() === scanned);
    let matchedSku = null;
    if (!product) { for (const p of prods) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === scanned); if (sku) { product = p; matchedSku = sku; break; } } }
    if (!product && (scanned.includes('_') || scanned.includes('-'))) { const parts = scanned.split(/[_-]/); if (parts.length >= 2) product = prods.find(p => p.design_code?.toUpperCase() === parts[0]); }
    if (!product) { toast.error(`Design "${scanned}" not found`); return; }
    const colors = getDesignColors(product);
    const skuSz = [...new Set((product.skus || []).map(s => s.size))];
    const sizes = [...new Set([...skuSz, ...(window.__g360_active_sizes || [])])];
    setRows(prev => {
      const emptyIdx = prev.findIndex(r => !r.product_id);
      const rowData = { product_id: product.id, design_code: product.design_code, designSearch: product.design_code, colors, sizes, product, showDesignDropdown: false, color: matchedSku?.color || '', size: matchedSku?.size || '' };
      if (emptyIdx >= 0) { const next = [...prev]; next[emptyIdx] = { ...next[emptyIdx], ...rowData }; return next; }
      return [...prev, { ...createEmptyRow(prev.length + 1), ...rowData }];
    });
    toast.success(`Scanned: ${product.design_code}${matchedSku ? ` | ${matchedSku.color} | ${matchedSku.size}` : ''}`);
  }, []);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const el = document.getElementById(scannerContainerId);
      if (!el) { setScannerActive(false); return; }
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 }, (t) => handleScannedCode(t), () => {}).catch(() => { toast.error('Camera not available. Use wireless scanner.'); setScannerActive(false); });
    }, 200);
  }, [handleScannedCode]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) { try { scannerRef.current.stop().then(() => { try { scannerRef.current.clear(); } catch(e) {} }).catch(() => {}); } catch(e) {} scannerRef.current = null; }
    setScannerActive(false);
  }, []);

  useEffect(() => { return () => { if (scannerRef.current) { try { scannerRef.current.stop().catch(() => {}); } catch(e) {} scannerRef.current = null; } }; }, []);
  // Auto-focus first design search on load
  useEffect(() => {
    if (dataLoaded) {
      setTimeout(() => { const input = document.querySelector('[data-testid="pe-design-search-0"]'); if (input) input.focus(); }, 300);
    }
  }, [dataLoaded]);

  // Close vendor dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close design dropdowns when clicking outside any design cell
  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.closest && e.target.closest('[data-pe-design-cell]')) return;
      setRows(prev => prev.map(r => (r.showDesignDropdown ? { ...r, showDesignDropdown: false } : r)));
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [vendRes, prodRes, whRes, brandRes, enumRes] = await Promise.all([
          api.get('/vendors'), api.get('/products'), api.get('/warehouses'),
          api.get('/brands'), api.get('/enums'),
        ]);
        setVendors(vendRes.data);
        setProducts(prodRes.data);
        setWarehouses(whRes.data);
        setBrands(brandRes.data);
        setEnums(enumRes.data);
        window.__g360_active_sizes = enumRes.data?.sizes || [];

        if (editPE) await prefillEdit(vendRes.data, prodRes.data, editPE, enumRes.data);
        else if (fromPO) prefillFromPO(vendRes.data, prodRes.data, fromPO, enumRes.data);
        setDataLoaded(true);
      } catch { toast.error('Failed to load data'); }
    };
    load();
  }, []);

  // Load vendor POs when vendor changes
  useEffect(() => {
    if (selectedVendor) {
      api.get(`/purchase-entries/vendor-pos/${selectedVendor}`).then(r => setVendorPOs(r.data)).catch(() => {});
    } else { setVendorPOs([]); }
  }, [selectedVendor]);

  const prefillEdit = async (vends, prods, pe, enumData) => {
    const vend = vends.find(v => v.id === pe.vendor_id);
    if (vend) {
      setSelectedVendor(vend.id);
      setVendorSearch(vend.name + (vend.city ? ` (${vend.city})` : ''));
    }
    setSelectedWarehouse(pe.warehouse_id || '');
    setEntryDate(pe.entry_date || '');
    setInvoiceNumber(pe.invoice_number || '');
    setChallanNumber(pe.challan_number || '');
    setPeNotes(pe.notes || '');
    setFreightCharges(pe.freight_charges || '');

    // The /products list is capped and excludes deleted designs; ensure every
    // product referenced by this PE is resolved (fetch any missing by id) so
    // colours and sizes populate correctly when editing.
    const prodMap = {};
    for (const p of prods) prodMap[p.id] = p;
    const missingIds = [...new Set((pe.items || []).map(i => i.product_id).filter(id => id && !prodMap[id]))];
    if (missingIds.length) {
      const fetched = await Promise.all(missingIds.map(id => api.get(`/products/${id}`).then(r => r.data).catch(() => null)));
      fetched.forEach(p => { if (p) prodMap[p.id] = p; });
    }

    const editRows = [];
    let sr = 1;
    for (const item of (pe.items || [])) {
      const prod = prodMap[item.product_id];
      const variantColors = [...new Set((item.variants || []).map(v => v.color).filter(Boolean))];
      const colors = prod ? getDesignColors(prod) : variantColors;
      const sizes = prod ? [...new Set(prod.skus.map(s => s.size))] : [...new Set((item.variants || []).map(v => v.size).filter(Boolean))];
      // Merge variants with same color+size
      const variantMap = {};
      for (const v of (item.variants || [])) {
        const vKey = `${v.color}_${v.size}`;
        if (variantMap[vKey]) {
          variantMap[vKey].qty_received = (variantMap[vKey].qty_received || 0) + (v.qty_received || 0);
        } else {
          variantMap[vKey] = { ...v };
        }
      }
      for (const v of Object.values(variantMap)) {
        let { groups, individualSizes: indSizes } = prod
          ? getSizeGroups(prod, v.color, enumData?.sizes)
          : { groups: [], individualSizes: (enumData?.sizes || []).map(s => ({ size: s, wsp: 0, mrp: 0 })) };
        // Guarantee the saved size is always selectable in edit, even if the
        // product's SKUs changed or the colour casing differs from the SKU.
        if (v.size && v.size !== 'both' && !groups.some(g => g.id === v.size) && !indSizes.some(s => s.size === v.size)) {
          indSizes = [...indSizes, { size: v.size, wsp: v.wsp_local || 0, mrp: v.wsp_all || 0 }];
        }
        editRows.push({
          sr, product_id: item.product_id, design_code: item.design_code,
          designSearch: item.design_code, color: v.color, size: v.size,
          qty: v.qty_received, cost: v.cost || 0, wsp_local: v.wsp_local || 0, wsp_all: v.wsp_all || 0,
          amount: Math.round((v.qty_received || 0) * (v.cost || 0)),
          colors, sizes, sizeGroups: groups, individualSizes: indSizes,
          product: prod, showDesignDropdown: false, productMissing: !prod,
          linked_po_id: item.linked_po_id, linked_po_item_id: item.linked_po_item_id,
        });
        sr++;
      }
    }
    if (editRows.length === 0) editRows.push(createEmptyRow(1));
    setRows(editRows);
  };

  const prefillFromPO = (vends, prods, poData, enumData) => {
    const vend = vends.find(v => v.id === poData.vendor_id);
    if (vend) {
      setSelectedVendor(vend.id);
      setVendorSearch(vend.name + (vend.city ? ` (${vend.city})` : ''));
    }
    const peRows = [];
    let sr = 1;
    // Collect all design codes to fetch previous pricing
    const designCodes = [...new Set((poData.items || []).map(i => i.design_code))];
    const pricingPromises = designCodes.map(dc =>
      api.get(`/purchase-entries/lookup-design/${dc}${vend ? `?vendor_id=${vend.id}` : ''}`).then(r => ({ dc, data: r.data })).catch(() => ({ dc, data: { found: false } }))
    );
    Promise.all(pricingPromises).then(results => {
      const pricingMap = {};
      const historyMap = {};
      results.forEach(r => {
        if (r.data.found) {
          pricingMap[r.dc] = r.data.pricing;
          historyMap[r.dc] = r.data.cost_history || [];
        }
      });
      for (const item of (poData.items || [])) {
        const prod = prods.find(p => p.id === item.product_id);
        const colors = prod ? getDesignColors(prod) : [];
        const sizes = prod ? [...new Set(prod.skus.map(s => s.size))] : [];
        for (const v of (item.variants || [])) {
          const pending = Math.max(0, (v.qty_ordered || 0) - (v.qty_received || 0));
          if (pending <= 0) continue;
          const { groups, individualSizes: indSizes } = prod ? getSizeGroups(prod, v.color, enumData?.sizes) : { groups: [], individualSizes: [] };
          const prev = pricingMap[item.design_code]?.[v.size];
          const cost = prev?.cost || v.tentative_cost || 0;
          const wspLocal = prev?.wsp_local || 0;
          const wspAll = prev?.wsp_all || 0;
          peRows.push({
            sr, product_id: item.product_id, design_code: item.design_code,
            designSearch: item.design_code, color: v.color, size: v.size,
            qty: pending, cost, wsp_local: wspLocal, wsp_all: wspAll,
            amount: Math.round(pending * cost),
            colors, sizes, sizeGroups: groups, individualSizes: indSizes,
            product: prod, showDesignDropdown: false,
            linked_po_id: poData.po_id, linked_po_item_id: item.id,
            _prevPricing: pricingMap[item.design_code] || null,
            _costHistory: historyMap[item.design_code] || [],
          });
          sr++;
        }
      }
      if (peRows.length === 0) peRows.push(createEmptyRow(1));
      setRows(peRows);
    });
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor.id);
    setVendorSearch(vendor.name + (vendor.city ? ` (${vendor.city})` : ''));
    setVendorDropdownOpen(false);
  };

  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return vendors.slice(0, 20);
    const q = vendorSearch.trim().toLowerCase();
    return vendors.filter(v =>
      v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q) || (/\d/.test(vendorSearch) && v.phone?.includes(q))
    ).slice(0, 20);
  }, [vendorSearch, vendors]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx] };
      row[field] = value;

      // SKU scan detection
      if (field === 'designSearch' && value && value.length >= 3) {
        const searchUpper = value.trim().toUpperCase();
        let matchedProduct = null, matchedSku = null;
        for (const p of products) { const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === searchUpper); if (sku) { matchedProduct = p; matchedSku = sku; break; } }
        if (!matchedProduct && (searchUpper.includes('_') || searchUpper.includes('-'))) { const parts = searchUpper.split(/[_-]/); if (parts.length >= 2) { matchedProduct = products.find(p => p.design_code?.toUpperCase() === parts[0]); if (matchedProduct) matchedSku = matchedProduct.skus?.find(s => s.sku_code?.toUpperCase() === searchUpper) || null; } }
        if (matchedProduct && matchedSku) {
          const existingIdx = next.findIndex((r, i) => i !== idx && r.product_id === matchedProduct.id && r.color === matchedSku.color && r.size === matchedSku.size);
          if (existingIdx >= 0) {
            const existing = { ...next[existingIdx] };
            existing.qty = (parseFloat(existing.qty) || 0) + 1;
            existing.amount = Math.round(existing.qty * (parseFloat(existing.cost) || 0));
            next[existingIdx] = existing;
            row.designSearch = '';
            next[idx] = row;
            toast.success(`${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size} → Qty: ${existing.qty}`);
            const sorted = sortScannedRows(next);
            setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="pe-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
            return sorted;
          }
          const colors = getDesignColors(matchedProduct);
          const { groups, individualSizes } = getSizeGroups(matchedProduct, matchedSku.color, enums.sizes);
          row.product_id = matchedProduct.id; row.design_code = matchedProduct.design_code; row.designSearch = matchedProduct.design_code;
          row.colors = colors; row.product = matchedProduct; row.showDesignDropdown = false;
          row.color = matchedSku.color; row.size = matchedSku.size;
          row.sizeGroups = groups; row.individualSizes = individualSizes; row.sizes = individualSizes.map(s => s.size);
          row.wsp_local = matchedSku.wsp || 0; row.wsp_all = matchedSku.mrp || 0;
          row.qty = 1;
          next[idx] = row;
          toast.success(`Scanned: ${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size}`);
          next.push(createEmptyRow(next.length + 1));
          const sorted = sortScannedRows(next);
          setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="pe-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
          return sorted;
        }
      }

      if (field === 'designSearch' && row.design_code && value !== row.design_code) {
        row.product_id = ''; row.design_code = ''; row.color = ''; row.size = '';
        row.colors = []; row.sizes = []; row.sizeGroups = []; row.individualSizes = [];
        row.product = null;
        row.cost = 0; row.wsp_local = 0; row.wsp_all = 0; row.qty = ''; row.amount = 0;
      }
      if (field === 'color' && row.product) {
        const { groups, individualSizes } = getSizeGroups(row.product, value, enums.sizes);
        row.sizeGroups = groups;
        row.individualSizes = individualSizes;
        row.sizes = individualSizes.map(s => s.size);
        if (groups.length > 0) {
          row.size = groups[0].id;
          row.wsp_local = groups[0].wsp;
          row.wsp_all = groups[0].mrp;
          // Auto-fill cost from previous PE pricing
          if (row._prevPricing) {
            const prev = row._prevPricing[groups[0].sizes[0]];
            if (prev) row.cost = prev.cost;
          }
        } else if (individualSizes.length > 0) {
          row.size = individualSizes[0].size;
          row.wsp_local = individualSizes[0].wsp;
          row.wsp_all = individualSizes[0].mrp;
          // Auto-fill cost from previous PE pricing
          if (row._prevPricing && row._prevPricing[individualSizes[0].size]) {
            const prev = row._prevPricing[individualSizes[0].size];
            row.cost = prev.cost;
            if (prev.wsp_local) row.wsp_local = prev.wsp_local;
            if (prev.wsp_all) row.wsp_all = prev.wsp_all;
          }
        }
      }
      if (field === 'size') {
        if (value === 'both') {
          row.wsp_local = '';
          row.wsp_all = '';
          // Auto-fill cost from previous PE pricing (use first group's first size)
          if (row._prevPricing) {
            const firstSize = (row.sizeGroups || [])[0]?.sizes?.[0];
            const prev = firstSize ? row._prevPricing[firstSize] : null;
            if (prev) row.cost = prev.cost;
          }
        } else {
          const grp = (row.sizeGroups || []).find(g => g.id === value);
          if (grp) {
            row.wsp_local = grp.wsp;
            row.wsp_all = grp.mrp;
            // Auto-fill cost from previous PE pricing for this group
            if (row._prevPricing) {
              const prev = row._prevPricing[grp.sizes[0]];
              if (prev) row.cost = prev.cost;
            }
          } else {
            const ind = (row.individualSizes || []).find(s => s.size === value);
            if (ind) { row.wsp_local = ind.wsp; row.wsp_all = ind.mrp; }
            // Auto-fill cost from previous PE pricing for this individual size
            if (row._prevPricing && row._prevPricing[value]) {
              const prev = row._prevPricing[value];
              row.cost = prev.cost;
              if (prev.wsp_local) row.wsp_local = prev.wsp_local;
              if (prev.wsp_all) row.wsp_all = prev.wsp_all;
            }
          }
        }
      }
      if (['qty', 'cost', 'size'].includes(field) || field === 'color') {
        const q = parseFloat(row.qty) || 0;
        const c = parseFloat(row.cost) || 0;
        if (row.size === 'both') {
          const totalCount = (row.sizeGroups || []).reduce((sum, g) => sum + g.count, 0);
          row.amount = Math.round(q * totalCount * c);
        } else {
          const grp = (row.sizeGroups || []).find(g => g.id === row.size);
          const mult = grp ? grp.count : 1;
          row.amount = Math.round(q * mult * c);
        }
      }
      next[idx] = row;
      return next;
    });
  };

  const selectDesign = (idx, product) => {
    const colors = getDesignColors(product);
    const allSizes = getSizeGroups(product, '', enums.sizes);
    const sizes = allSizes.individualSizes.length > 0
      ? allSizes.individualSizes.map(s => s.size)
      : [...new Set((product.skus || []).map(s => s.size)), ...(enums.sizes || [])].filter((v, i, a) => a.indexOf(v) === i);
    setRows(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, sizes, product,
        showDesignDropdown: false, color: '', size: '', sizeGroups: [], individualSizes: [],
        cost: 0, wsp_local: 0, wsp_all: 0, amount: 0, _prevPricing: null, productMissing: false,
      };
      return next;
    });
    // Fetch previous PE pricing for this design
    const vendorParam = selectedVendor ? `?vendor_id=${selectedVendor}` : '';
    api.get(`/purchase-entries/lookup-design/${product.design_code}${vendorParam}`).then(res => {
      if (res.data.found) {
        setRows(prev => {
          const next = [...prev];
          if (next[idx] && next[idx].product_id === product.id) {
            next[idx] = { ...next[idx], _prevPricing: res.data.pricing || null, _costHistory: res.data.cost_history || [] };
          }
          return next;
        });
      }
    }).catch(() => {});
    setSubstituteDialog({ rowIdx: idx, product });
    setSubstituteSearch('');
  };

  const confirmDesign = () => setSubstituteDialog(null);

  const selectSubstitute = (product) => {
    if (!substituteDialog) return;
    const idx = substituteDialog.rowIdx;
    const colors = getDesignColors(product);
    const sizes = [...new Set(product.skus.map(s => s.size))];
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], product_id: product.id, design_code: product.design_code, designSearch: product.design_code, colors, sizes, product, color: '', size: '', sizeGroups: [], individualSizes: [], cost: 0, wsp_local: 0, wsp_all: 0, amount: 0 };
      return next;
    });
    setSubstituteDialog({ rowIdx: idx, product });
    setSubstituteSearch('');
  };

  const addRow = () => setRows(prev => [...prev, createEmptyRow(prev.length + 1)]);
  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sr: i + 1 })));
  };

  const filteredDesigns = (search) => {
    if (!search.trim()) return products.slice(0, 15);
    const q = search.trim().toLowerCase();
    return products.filter(p => p.design_code.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q)).slice(0, 15);
  };

  const submitPE = async () => {
    if (!selectedVendor) { toast.error('Select a vendor'); return; }
    if (!selectedWarehouse) { toast.error('Select a warehouse'); return; }
    if (!invoiceNumber?.trim() && !challanNumber?.trim()) { toast.error('Either Invoice Number or Challan Number is required'); return; }
    const validRows = rows.filter(r => r.product_id && r.color && r.size && parseInt(r.qty) > 0);
    if (validRows.length === 0) { toast.error('Add at least one item with color, size, and qty'); return; }

    // Check WSP — for "both" we validate per-group, for set/individual we validate the row
    for (const r of validRows) {
      if (r.size === 'both') {
        // WSP is per-group, auto-filled — skip validation for both
      } else {
        if (!r.wsp_local || parseFloat(r.wsp_local) <= 0 || !r.wsp_all || parseFloat(r.wsp_all) <= 0) {
          toast.error(`WSP (L) and WSP are required for all rows (check ${r.design_code} / ${r.color} / ${r.size})`);
          return;
        }
      }
    }

    // Group rows by product_id
    const itemsMap = {};
    for (const r of validRows) {
      if (!itemsMap[r.product_id]) {
        itemsMap[r.product_id] = {
          product_id: r.product_id, design_code: r.design_code,
          linked_po_id: r.linked_po_id, linked_po_item_id: r.linked_po_item_id,
          variants: [],
        };
      }
      const totalQty = parseInt(r.qty) || 0;
      if (r.size === 'both') {
        // Expand all groups
        (r.sizeGroups || []).forEach(grp => {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[r.product_id].variants.push({
                size, color: r.color, qty_received: totalQty,
                cost: parseFloat(r.cost) || 0,
                wsp_local: grp.wsp || 0,
                wsp_all: grp.mrp || 0,
                quality_status: 'ok',
              });
            }
          });
        });
      } else {
        const grp = (r.sizeGroups || []).find(g => g.id === r.size);
        if (grp) {
          grp.sizes.forEach(size => {
            if (totalQty > 0) {
              itemsMap[r.product_id].variants.push({
                size, color: r.color, qty_received: totalQty,
                cost: parseFloat(r.cost) || 0,
                wsp_local: parseFloat(r.wsp_local) || 0,
                wsp_all: parseFloat(r.wsp_all) || 0,
                quality_status: 'ok',
              });
            }
          });
        } else {
          // Individual size
          if (totalQty > 0 && r.size) {
            itemsMap[r.product_id].variants.push({
              size: r.size, color: r.color, qty_received: totalQty,
              cost: parseFloat(r.cost) || 0,
              wsp_local: parseFloat(r.wsp_local) || 0,
              wsp_all: parseFloat(r.wsp_all) || 0,
              quality_status: 'ok',
            });
          }
        }
      }
    }

    // Deduplicate variants: merge same size+color by summing qty
    for (const item of Object.values(itemsMap)) {
      const vMap = {};
      for (const v of item.variants) {
        const vKey = `${v.size}_${v.color}`;
        if (vMap[vKey]) {
          vMap[vKey].qty_received += v.qty_received;
        } else {
          vMap[vKey] = { ...v };
        }
      }
      item.variants = Object.values(vMap);
    }

    const payload = {
      vendor_id: selectedVendor,
      warehouse_id: selectedWarehouse,
      entry_date: entryDate,
      invoice_number: invoiceNumber || null,
      challan_number: challanNumber || null,
      items: Object.values(itemsMap),
      notes: peNotes || null,
      freight_charges: parseFloat(freightCharges) || 0,
    };

    setSaving(true);
    try {
      if (editPE) {
        await api.put(`/purchase-entries/${editPE.id}`, payload);
        toast.success(`GRN ${editPE.grn_number} updated!`);
        clearDraft();
        navigate(`/purchase-entry/${editPE.id}`);
      } else {
        const res = await api.post('/purchase-entries', payload);
        toast.success(`GRN ${res.data.grn_number} created!`);
        clearDraft();
        navigate('/purchase-entry');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';
  const { grandTotal, grandQty } = computeGrandTotals(rows);

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="create-pe-page">
      <PEHeader editPE={editPE} saving={saving} onCancel={() => navigate('/purchase-entry')} onSave={submitPE} />

      <VendorDetailsCard
        vendorRef={vendorRef}
        vendorSearch={vendorSearch} setVendorSearch={setVendorSearch} setSelectedVendor={setSelectedVendor}
        vendorDropdownOpen={vendorDropdownOpen} setVendorDropdownOpen={setVendorDropdownOpen}
        filteredVendors={filteredVendors} selectVendor={selectVendor}
        warehouses={warehouses} selectedWarehouse={selectedWarehouse} setSelectedWarehouse={setSelectedWarehouse}
        entryDate={entryDate} setEntryDate={setEntryDate}
        invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
        challanNumber={challanNumber} setChallanNumber={setChallanNumber}
        peNotes={peNotes} setPeNotes={setPeNotes}
      />

      <PEScannerCard scannerActive={scannerActive} startScanner={startScanner} stopScanner={stopScanner} scannerContainerId={scannerContainerId} />

      <InvoiceTable
        rows={rows} products={products}
        updateRow={updateRow} selectDesign={selectDesign} removeRow={removeRow} addRow={addRow}
        filteredDesigns={filteredDesigns} getBrandName={getBrandName} handleTableKeyDown={handleTableKeyDown}
        setRows={setRows} setCostHistoryPopup={setCostHistoryPopup}
        grandQty={grandQty} grandTotal={grandTotal}
      />

      <FreightFooter freightCharges={freightCharges} setFreightCharges={setFreightCharges} grandTotal={grandTotal} />

      <SubstituteDialog
        substituteDialog={substituteDialog} confirmDesign={confirmDesign} getBrandName={getBrandName}
        substituteSearch={substituteSearch} setSubstituteSearch={setSubstituteSearch}
        filteredDesigns={filteredDesigns} selectSubstitute={selectSubstitute}
      />

      <CostHistoryDialog costHistoryPopup={costHistoryPopup} setCostHistoryPopup={setCostHistoryPopup} />
    </div>
  );
};

export default CreatePurchaseEntry;
